import { useRef, useState } from "react";
import { History, Trash2, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const HistoryUpload = ({ project, onChanged, disabled }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [year, setYear] = useState("");
  const { can } = useAuth();
  const years = Object.entries(project.history || {}).sort((a, b) => b[0] - a[0]);
  const baseYear = project.dataset?.actual_year;
  const [jobMsg, setJobMsg] = useState("");
  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (year) fd.append("year", year);
      await api.post(`/projects/${project.id}/history`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      toast.info("Files received — reading and mapping accounts in the background…");
      let data = null;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = (await api.get(`/projects/${project.id}/history/status`)).data;
        setJobMsg(st.message || "");
        if (st.status === "done") { data = st; break; }
        if (st.status === "failed") throw new Error(st.message || "History upload failed");
      }
      if (!data) throw new Error("Still processing — refresh the page in a minute");
      toast.success(`Prior-year actuals ${data.year} loaded — ${data.matched} lines matched (${(data.months || []).map((m) => MONTHS[m - 1]).join(", ")})${data.ai_pairs ? `, ${data.ai_pairs} mapped by AI from a different format` : ""}${data.forecast_method === "history_trend" ? ". Forecast method set to Historical trend" : ""}`);
      onChanged();
    } catch (e) { toast.error(e?.response ? errorText(e) : e.message); } finally { setBusy(false); setJobMsg(""); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async (y) => {
    if (!window.confirm(`Remove prior-year actuals ${y}?`)) return;
    try { await api.delete(`/projects/${project.id}/history/${y}`); toast.success(`Removed ${y}`); onChanged(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="history-upload">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><History size={13} className="text-emerald-600" /> Prior-year actuals (reference)</p>
      <p className="text-sm text-slate-600 mt-2">Upload the P&amp;L files of {baseYear ? `${baseYear - 1}, ${baseYear - 2}` : "the previous years"} (and earlier). They power the seasonal, year-over-year and least-squares forecast methods and appear as reference columns in the budget table. Put the month and year in the file name, e.g. "SAMA Mei {baseYear ? baseYear - 1 : 2025}.xls". Monthly reports of the same year are merged — add months over time; a re-uploaded month replaces the older report.</p>
      <ul className="mt-3 space-y-2" data-testid="history-years">
        {years.map(([y, h]) => (
          <li key={y} className="flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid={`history-year-${y}`}>
            <div><span className="font-mono font-semibold text-slate-900">{y}</span> <span className="text-xs text-slate-500">· {h.matched} lines · {(h.months || []).map((m) => MONTHS[m - 1]).join(", ")}</span></div>
            {can("upload") && !disabled && <button onClick={() => remove(y)} className="text-slate-400 hover:text-red-600" title="Remove" data-testid={`history-remove-${y}`}><Trash2 size={14} /></button>}
          </li>
        ))}
        {!years.length && <li className="text-xs text-slate-400">No prior-year data yet{project.dataset ? "" : " — upload the current-year actuals first"}.</li>}
      </ul>
      {can("upload") && project.dataset && !disabled && (
        <>
          <input ref={inputRef} type="file" multiple accept=".xlsx,.xls,.xlsm,.csv" className="hidden" data-testid="history-file-input" onChange={(e) => upload(e.target.files)} />
          <div className="mt-3 flex gap-2">
            <input type="number" placeholder={`Year (auto)`} value={year} onChange={(e) => setYear(e.target.value)} data-testid="history-year-input" className="w-28 h-9 rounded-md border border-slate-300 px-2 text-sm font-mono" />
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} className="h-9 flex-1" data-testid="history-upload-button">
              <UploadCloud size={14} className="mr-2" /> {busy ? (jobMsg || "Reading prior-year files...") : "Add prior-year files"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
